import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Upload, ArrowLeft, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { bookingId, bookingReference, amount, clientEmail, consultationType } = location.state || {};

    const [loading, setLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('gcash');
    const [referenceNumber, setReferenceNumber] = useState('');
    const [proofFileId, setProofFileId] = useState('');
    const [copied, setCopied] = useState({});

    const paymentDetails = {
        gcash: { 
            number: '09622526114', 
            name: 'GCash',
            qrCode: 'https://customer-assets.emergentagent.com/job_vetmonato/artifacts/8rmcw2xl_GCash-MyQR-22122025062656.PNG.jpg'
        },
        maya: { 
            number: '+639770072496', 
            name: 'Maya',
            qrCode: 'https://customer-assets.emergentagent.com/job_vetmonato/artifacts/uyq08u56_1000111403.jpg'
        },
        bank_transfer: { 
            account: '7591068636', 
            bank: 'RCBC', 
            name: 'Bank Transfer',
            qrCode: 'https://customer-assets.emergentagent.com/job_vetmonato/artifacts/bcbi9v78_1000111402.jpg'
        },
        instapay: { 
            name: 'InstaPay',
            qrCode: 'https://customer-assets.emergentagent.com/job_vetmonato/artifacts/6qnlp7s1_1000111404.jpg'
        }
    };

    useEffect(() => {
        if (!bookingId) {
            toast.error('No booking found');
            navigate('/');
        }
    }, [bookingId, navigate]);

    const { getRootProps, getInputProps } = useDropzone({
        accept: { 'image/*': ['.png', '.jpg', '.jpeg'] },
        maxSize: 5242880,
        maxFiles: 1,
        onDrop: async (acceptedFiles) => {
            if (acceptedFiles.length === 0) return;
            setLoading(true);
            try {
                const formData = new FormData();
                formData.append('file', acceptedFiles[0]);
                const response = await axios.post(`${API}/upload`, formData);
                setProofFileId(response.data.file_id);
                toast.success('Payment proof uploaded');
            } catch (error) {
                toast.error('Failed to upload proof');
            } finally {
                setLoading(false);
            }
        }
    });

    const handleCopy = (text, key) => {
        navigator.clipboard.writeText(text);
        setCopied({ ...copied, [key]: true });
        toast.success('Copied to clipboard');
        setTimeout(() => setCopied({ ...copied, [key]: false }), 2000);
    };

    const [paymentSubmitted, setPaymentSubmitted] = useState(false);

    const handleSubmitPayment = async () => {
        if (!referenceNumber) {
            toast.error('Please enter your payment reference number');
            return;
        }

        if (!proofFileId) {
            toast.error('Please upload payment proof screenshot');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API}/payments/confirm`, {
                booking_id: bookingId,
                payment_method: paymentMethod,
                reference_number: referenceNumber,
                proof_file_id: proofFileId
            });

            setPaymentSubmitted(true);
            toast.success('Payment proof submitted successfully');
        } catch (error) {
            toast.error('Failed to submit payment. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#fcf6e3' }}>
            <header className="glass-header py-4">
                <div className="max-w-6xl mx-auto px-6 flex justify-between items-center">
                    <div>
                        <h1 
                            className="text-xl sm:text-2xl font-bold cursor-pointer" 
                            style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                            onClick={() => navigate('/')}
                        >
                            MVC VET TELECARE
                        </h1>
                        <p className="text-sm" style={{ color: '#d9a520' }}>Licensed Veterinarians</p>
                    </div>
                    <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
                        <ArrowLeft className="mr-2" /> Back
                    </Button>
                </div>
            </header>

            <div className="max-w-3xl mx-auto px-6 py-12">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                    {paymentSubmitted ? (
                        <div className="card p-8 text-center">
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', stiffness: 200 }}
                                className="flex justify-center mb-6"
                            >
                                <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: '#d9a520' }}>
                                    <Check className="w-12 h-12 text-white" />
                                </div>
                            </motion.div>
                            <h2 
                                className="text-3xl font-bold mb-4" 
                                style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                            >
                                Payment Proof Submitted
                            </h2>
                            <p className="text-lg mb-6" style={{ color: '#2d2a2a' }}>
                                Thank you! Your payment is being verified.
                            </p>
                            <div className="bg-muted p-6 rounded-lg mb-6">
                                <p className="text-sm text-muted-foreground mb-2">Booking Reference</p>
                                <p className="text-2xl font-bold" style={{ color: '#701a25' }}>{bookingReference}</p>
                            </div>
                            <div className="space-y-3 text-left mb-8">
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full flex items-center justify-center mt-1" style={{ backgroundColor: '#d9a520' }}>
                                        <span className="text-white text-sm font-bold">1</span>
                                    </div>
                                    <div>
                                        <p className="font-semibold" style={{ color: '#701a25' }}>Payment Verification</p>
                                        <p className="text-sm text-muted-foreground">
                                            We are verifying your payment. This usually takes 15-30 minutes during business hours.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full flex items-center justify-center mt-1" style={{ backgroundColor: '#d9a520' }}>
                                        <span className="text-white text-sm font-bold">2</span>
                                    </div>
                                    <div>
                                        <p className="font-semibold" style={{ color: '#701a25' }}>Email Confirmation</p>
                                        <p className="text-sm text-muted-foreground">
                                            Once verified, you'll receive a confirmation email at <strong>{clientEmail}</strong> with your consultation link.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full flex items-center justify-center mt-1" style={{ backgroundColor: '#d9a520' }}>
                                        <span className="text-white text-sm font-bold">3</span>
                                    </div>
                                    <div>
                                        <p className="font-semibold" style={{ color: '#701a25' }}>Join Consultation</p>
                                        <p className="text-sm text-muted-foreground">
                                            Use the link in your email to join the consultation at your scheduled time.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-secondary/10 p-4 rounded-lg mb-6">
                                <p className="text-sm font-semibold" style={{ color: '#701a25' }}>
                                    💡 Pro Tip: Check your spam/junk folder if you don't see our email within 30 minutes.
                                </p>
                            </div>
                            <Button 
                                onClick={() => navigate('/')}
                                className="btn-primary"
                                data-testid="back-to-home-btn"
                            >
                                Back to Home
                            </Button>
                        </div>
                    ) : (
                    <div className="card p-8">
                        <h2 
                            className="text-3xl font-bold mb-2" 
                            style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                            data-testid="payment-page-title"
                        >
                            Payment Instructions
                        </h2>
                        <p className="text-muted-foreground mb-6">
                            Booking Reference: <strong>{bookingReference}</strong>
                        </p>

                        <div className="bg-secondary/10 p-6 rounded-lg mb-8">
                            <p className="text-3xl font-bold text-center" style={{ color: '#701a25' }} data-testid="payment-amount">
                                ₱{amount}
                            </p>
                            <p className="text-center text-sm text-muted-foreground mt-2">
                                {consultationType === 'general' ? 'General Consultation' : 'Emergency Consultation'}
                            </p>
                        </div>

                        <Tabs value={paymentMethod} onValueChange={setPaymentMethod} className="w-full">
                            <TabsList className="grid w-full grid-cols-4 mb-6">
                                <TabsTrigger value="gcash" data-testid="gcash-tab">GCash</TabsTrigger>
                                <TabsTrigger value="maya" data-testid="maya-tab">Maya</TabsTrigger>
                                <TabsTrigger value="bank_transfer" data-testid="bank-tab">Bank</TabsTrigger>
                                <TabsTrigger value="instapay" data-testid="instapay-tab">InstaPay</TabsTrigger>
                            </TabsList>

                            <TabsContent value="gcash" data-testid="gcash-content">
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <img 
                                                src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5c/GCash_logo.svg/512px-GCash_logo.svg.png" 
                                                alt="GCash" 
                                                className="h-6"
                                            />
                                            GCash Payment
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="bg-muted p-6 rounded-lg text-center">
                                            <p className="text-sm text-muted-foreground mb-4">Scan QR Code to Pay ₱{amount}</p>
                                            <img 
                                                src={paymentDetails.gcash.qrCode}
                                                alt="GCash QR Code"
                                                className="mx-auto max-w-xs w-full rounded-lg shadow-lg"
                                            />
                                            <p className="text-xs text-muted-foreground mt-4">Or send to: {paymentDetails.gcash.number}</p>
                                        </div>
                                        <ol className="list-decimal list-inside space-y-2 text-sm">
                                            <li>Open your GCash app</li>
                                            <li>Tap &quot;Scan QR&quot; and scan the code above</li>
                                            <li>Enter amount: ₱{amount}</li>
                                            <li>Complete the transaction</li>
                                            <li>Take screenshot and upload below with reference number</li>
                                        </ol>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            <TabsContent value="maya" data-testid="maya-content">
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <img 
                                                src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/4f/Maya_%28payments_app%29.svg/512px-Maya_%28payments_app%29.svg.png" 
                                                alt="Maya" 
                                                className="h-6"
                                            />
                                            Maya Payment
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="bg-muted p-6 rounded-lg text-center">
                                            <p className="text-sm text-muted-foreground mb-4">Scan QR Code to Pay ₱{amount}</p>
                                            <img 
                                                src={paymentDetails.maya.qrCode}
                                                alt="Maya QR Code"
                                                className="mx-auto max-w-xs w-full rounded-lg shadow-lg"
                                            />
                                            <p className="text-xs text-muted-foreground mt-4">Or send to: {paymentDetails.maya.number}</p>
                                        </div>
                                        <ol className="list-decimal list-inside space-y-2 text-sm">
                                            <li>Open your Maya app</li>
                                            <li>Tap &quot;Scan QR&quot; and scan the code above</li>
                                            <li>Enter amount: ₱{amount}</li>
                                            <li>Complete the transaction</li>
                                            <li>Take screenshot and upload below with reference number</li>
                                        </ol>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            <TabsContent value="bank_transfer" data-testid="bank-content">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>RCBC Bank Transfer</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="bg-muted p-6 rounded-lg text-center">
                                            <p className="text-sm text-muted-foreground mb-4">Scan QR Code to Pay ₱{amount}</p>
                                            <img 
                                                src={paymentDetails.bank_transfer.qrCode}
                                                alt="RCBC QR Code"
                                                className="mx-auto max-w-xs w-full rounded-lg shadow-lg"
                                            />
                                            <p className="text-xs text-muted-foreground mt-4">Account: {paymentDetails.bank_transfer.account}</p>
                                        </div>
                                        <ol className="list-decimal list-inside space-y-2 text-sm">
                                            <li>Open your bank&apos;s app or InstaPay</li>
                                            <li>Scan QR code above</li>
                                            <li>Enter amount: ₱{amount}</li>
                                            <li>Complete the transaction</li>
                                            <li>Take screenshot and upload below with reference number</li>
                                        </ol>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            <TabsContent value="instapay" data-testid="instapay-content">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>InstaPay Payment</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="bg-muted p-6 rounded-lg text-center">
                                            <p className="text-sm text-muted-foreground mb-4">Scan QR Code to Pay ₱{amount}</p>
                                            <img 
                                                src={paymentDetails.instapay.qrCode}
                                                alt="InstaPay QR Code"
                                                className="mx-auto max-w-xs w-full rounded-lg shadow-lg"
                                            />
                                            <p className="text-xs text-muted-foreground mt-4">Compatible with all major banks</p>
                                        </div>
                                        <ol className="list-decimal list-inside space-y-2 text-sm">
                                            <li>Open your banking app (any bank with InstaPay)</li>
                                            <li>Select &quot;Scan QR&quot; or &quot;InstaPay&quot;</li>
                                            <li>Scan the QR code above</li>
                                            <li>Enter amount: ₱{amount}</li>
                                            <li>Complete payment and take screenshot with reference number</li>
                                        </ol>
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </Tabs>

                        <div className="mt-8 space-y-4">
                            <div>
                                <Label htmlFor="reference">Transaction Reference Number *</Label>
                                <Input
                                    id="reference"
                                    data-testid="reference-input"
                                    value={referenceNumber}
                                    onChange={(e) => setReferenceNumber(e.target.value)}
                                    placeholder="Enter reference/transaction number"
                                />
                            </div>

                            <div>
                                <Label>Upload Payment Proof (Screenshot) *</Label>
                                <div 
                                    {...getRootProps()} 
                                    data-testid="proof-dropzone"
                                    className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                                >
                                    <input {...getInputProps()} />
                                    <Upload className="w-10 h-10 mx-auto mb-2" style={{ color: '#d9a520' }} />
                                    <p className="text-sm text-muted-foreground">
                                        {proofFileId ? '✓ Proof uploaded' : 'Click to upload payment screenshot'}
                                    </p>
                                </div>
                            </div>

                            <Button 
                                onClick={handleSubmitPayment}
                                disabled={loading || !referenceNumber || !proofFileId}
                                className="btn-primary w-full mt-6"
                                data-testid="submit-payment-btn"
                            >
                                {loading ? 'Submitting...' : 'Submit Payment Proof'}
                            </Button>
                        </div>

                        <p className="text-xs text-muted-foreground text-center mt-6">
                            Your booking will be confirmed once payment is verified. You will receive the consultation link via email.
                        </p>
                    </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default PaymentPage;
